import * as React from 'react';
import { useCallback, useState, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import * as Immutable from 'immutable';

import type { Message } from 'views/components/messagelist/Types';
import LogViewWidget from 'logview/components/LogViewWidget';
import type { LogViewMessage } from 'logview/types';
import type DataLakeWidget from 'data-lake/logic/DataLakeWidget';
import type DataLakeWidgetConfig from 'data-lake/logic/DataLakeWidgetConfig';
import useOnBrowserNavigation from 'data-lake/hooks/useOnBrowserNavigation';
import type FieldType from 'views/logic/fieldtypes/FieldType';
import TypeSpecificValue from 'views/components/TypeSpecificValue';
import CustomHighlighting from 'views/components/highlighting/CustomHighlighting';
import { PAGE_SIZE, MAX_LOADED_MESSAGES, INITIAL_PAGE, MAX_PAGE } from 'data-lake/preview/Constants';
import BulkActions from 'data-lake/preview/BulkActions';
import { Alert } from 'components/bootstrap';
import { pollJob } from 'views/logic/slices/executeJobResult';
import type { MostRecentJobIds } from 'data-lake/preview/Types';
import formatBackendMessages from 'data-lake/preview/utils/formatBackendMessages';
import DLLogViewWidgetContainer from 'data-lake/preview/DLLogViewWidgetContainer';
import type { AbsoluteTimeRange } from 'views/logic/queries/Query';
import { useSendWidgetConfigUpdateTelemetry } from 'views/components/widgets/telemety';
import stopSearchResultsPolling from 'data-lake/preview/utils/stopSearchResultsPolling';
import { isPermitted } from 'util/PermissionsMixin';
import useCurrentUser from 'hooks/useCurrentUser';
import type FetchError from 'logic/errors/FetchError';
import UserNotification from 'util/UserNotification';
import PreviewIntro from 'data-lake/preview/PreviewIntro';
import usePreviewLayout from 'data-lake/preview/hooks/usePreviewLayout';
import useFieldTypes from 'data-lake/hooks/useFieldTypes';
import useSelectedEntities from 'components/common/EntityDataTable/hooks/useSelectedEntities';

const MessageLimitAlert = styled(Alert)(
  ({ theme }) => css`
    margin-top: ${theme.spacings.sm};
  `,
);

const initialLoadedAllMessages = (initialPageTotal: number, messages: Array<LogViewMessage>) =>
  initialPageTotal < PAGE_SIZE || (initialPageTotal > PAGE_SIZE && initialPageTotal === messages.length);

const ResetListState = ({
  resetListState,
  messages,
  initialPageTotal,
  setLoadedAllMessages,
}: {
  resetListState: () => void;
  messages: Array<LogViewMessage>;
  initialPageTotal: number;
  setLoadedAllMessages: (loadedALl: boolean) => void;
}) => {
  const prevMessages = useRef(messages);
  const { setSelectedEntities } = useSelectedEntities();

  // reset list state after manual search execution
  useEffect(() => {
    if (prevMessages && prevMessages.current !== messages) {
      setLoadedAllMessages(initialLoadedAllMessages(initialPageTotal, messages));
      setSelectedEntities([]);
      resetListState();
      prevMessages.current = messages;
    }
  }, [initialPageTotal, messages, resetListState, setLoadedAllMessages, setSelectedEntities]);

  useOnBrowserNavigation(resetListState);

  return null;
};

type VisualisationProps = {
  editing: boolean;
  messages: Array<LogViewMessage>;
  onChangeConfig: (newConfig: DataLakeWidgetConfig) => Promise<unknown>;
  widget: DataLakeWidget;
  mostRecentJobIds: MostRecentJobIds | undefined;
  initialPageTotal: number;
  setLoading: (loading: boolean) => void;
  isFetchingFirstPage?: boolean;
  hasExecutedSearch?: boolean;
};

const Visualisation = ({
  messages,
  widget,
  editing,
  mostRecentJobIds,
  onChangeConfig,
  initialPageTotal,
  setLoading,
  isFetchingFirstPage = undefined,
  hasExecutedSearch = undefined,
}: VisualisationProps) => {
  const { data: fieldTypes } = useFieldTypes();
  const currentUser = useCurrentUser();
  const [loadedAllMessages, setLoadedAllMessages] = useState(initialLoadedAllMessages(initialPageTotal, messages));
  const { defaultFields, timestampField } = usePreviewLayout();

  const onLoadMessages = useCallback(
    async (after: { perPage: number; page: number }) => {
      const nextPage = after.page + 1;
      if (after.page * after.perPage >= MAX_LOADED_MESSAGES) {
        setLoadedAllMessages(true);

        return Promise.resolve({ messages: [], after });
      }

      const result = await pollJob({
        stopPolling: (progress) => stopSearchResultsPolling(nextPage, progress),
        jobIds: {
          nodeId: mostRecentJobIds.nodeId,
          asyncSearchId: mostRecentJobIds.asyncSearchId,
        },
        result: null,
        page: nextPage,
        perPage: PAGE_SIZE,
      }).catch((error: FetchError) =>
        UserNotification.error(`Loading further messages failed with status: ${error.message}`),
      );

      if (!result) {
        return Promise.resolve({ messages: [], after });
      }

      const queryId = Object.keys(result.results)[0];
      const searchTypeId = Object.keys(result.results[queryId].search_types)[0];
      const { messages: newMessages } = result.results[queryId].search_types[searchTypeId];

      if (newMessages.length === 0) {
        setLoadedAllMessages(true);
      }

      return Promise.resolve({
        messages: formatBackendMessages(newMessages),
        after: { page: nextPage, perPage: PAGE_SIZE },
      });
    },
    [mostRecentJobIds?.asyncSearchId, mostRecentJobIds?.nodeId],
  );

  const onLoadMessage = useCallback((message: { message: { [fieldName: string]: unknown } }) => {
    const filteredFields = Object.fromEntries(
      Object.entries(message.message).filter(([_, value]) => value !== null && value !== undefined),
    );

    return Promise.resolve({
      formatted_fields: filteredFields,
      filtered_fields: filteredFields,
      fields: filteredFields,
    } as unknown as Message);
  }, []);

  const renderFieldValue = useCallback(
    (field: string, fieldType: FieldType, value: string) => (
      <CustomHighlighting field={field} value={value}>
        <TypeSpecificValue field={field} value={value} type={fieldType} />
      </CustomHighlighting>
    ),
    [],
  );

  const bottomSection = useCallback((loadedMessagesCount: number, bottomPageId: number) => {
    if (loadedMessagesCount !== MAX_LOADED_MESSAGES || bottomPageId !== MAX_LOADED_MESSAGES / PAGE_SIZE) {
      return null;
    }

    return (
      <MessageLimitAlert bsStyle="warning" className="no-bm">
        Your Preview Search has reached the maximum limit of {MAX_LOADED_MESSAGES} messages. To find more relevant logs,
        try refining your search with more specific parameters.
      </MessageLimitAlert>
    );
  }, []);

  const bulkSelection = isPermitted(currentUser.permissions, 'data_lake_archive:restore')
    ? {
        actions: <BulkActions<{ sourceId: string }> retrievalPayload={{ sourceId: widget.streams[0] }} />,
      }
    : undefined;

  return (
    <LogViewWidget<DataLakeWidgetConfig>
      timestampField={timestampField}
      isFetchingFirstPage={isFetchingFirstPage}
      defaultFields={defaultFields}
      config={widget.config}
      bottomSection={bottomSection}
      initialAfter={{ page: INITIAL_PAGE, perPage: PAGE_SIZE }}
      infiniteScrollDirection="DOWN"
      messages={messages}
      onLoadMessages={onLoadMessages}
      renderFieldValue={renderFieldValue}
      editing={editing}
      loadedAllMessages={(_loadedMessagesCount, _after, bottomPageId) => loadedAllMessages || bottomPageId === MAX_PAGE}
      onLoadMessage={onLoadMessage}
      setLoadingState={setLoading}
      fields={Immutable.List(fieldTypes)}
      onChangeConfig={(config) => onChangeConfig(config)}
      bulkSelection={bulkSelection}
      placeholder={!hasExecutedSearch && !isFetchingFirstPage && <PreviewIntro />}
      hasExecutedSearch={hasExecutedSearch}>
      {({ resetListState }) => (
        <ResetListState
          resetListState={resetListState}
          messages={messages}
          setLoadedAllMessages={setLoadedAllMessages}
          initialPageTotal={initialPageTotal}
        />
      )}
    </LogViewWidget>
  );
};

type Props = {
  editing: boolean;
  effectiveTimerange: AbsoluteTimeRange;
  initialPageTotal: number;
  isFetchingFirstPage: boolean;
  messages: Array<LogViewMessage>;
  mostRecentJobIds: MostRecentJobIds | undefined;
  onChangeWidget: (newWidget: DataLakeWidget) => Promise<unknown>;
  onToggleEdit: () => void;
  widget: DataLakeWidget;
  hasExecutedSearch: boolean;
};

const DLLogViewWidget = ({
  editing,
  effectiveTimerange,
  initialPageTotal,
  isFetchingFirstPage,
  messages,
  mostRecentJobIds,
  onChangeWidget,
  onToggleEdit,
  widget,
  hasExecutedSearch,
}: Props) => {
  const sendWidgetConfigUpdateTelemetry = useSendWidgetConfigUpdateTelemetry();
  const [loading, setLoading] = useState(false);

  const onChangeConfig = useCallback(
    (newConfig: DataLakeWidgetConfig) => {
      sendWidgetConfigUpdateTelemetry();

      return onChangeWidget(widget.toBuilder().config(newConfig).build());
    },
    [onChangeWidget, sendWidgetConfigUpdateTelemetry, widget],
  );

  return (
    <DLLogViewWidgetContainer
      hasExecutedSearch={hasExecutedSearch}
      widget={widget}
      editing={editing}
      isFetchingFirstPage={isFetchingFirstPage}
      loading={loading}
      onToggleEdit={onToggleEdit}
      effectiveTimerange={effectiveTimerange}
      onChangeConfig={onChangeConfig}
      onChangeWidget={onChangeWidget}>
      <Visualisation
        hasExecutedSearch={hasExecutedSearch}
        isFetchingFirstPage={isFetchingFirstPage}
        setLoading={setLoading}
        editing={editing}
        messages={messages}
        onChangeConfig={onChangeConfig}
        widget={widget}
        mostRecentJobIds={mostRecentJobIds}
        initialPageTotal={initialPageTotal}
      />
    </DLLogViewWidgetContainer>
  );
};

export default DLLogViewWidget;
